import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthState } from '@app/core/auth/states/auth.state';
import { App } from '@app/core/models/app.model';
import { SetCurrentApp } from '@app/core/states/app.actions';
import { AppState } from '@app/core/states/app.state';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { UiModule } from '@app/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, Subscription, distinctUntilChanged, filter } from 'rxjs';
import { AndroidPublishingCardComponent } from '../components/android-publishing-card/android-publishing-card.component';
import { IosPublishingCardComponent } from '../components/ios-publishing-card/ios-publishing-card.component';
import { PaidPublishingBannerComponent } from '../components/paid-publishing-banner/paid-publishing-banner.component';
import { PublishingHeaderAdminViewComponent } from '../components/publishing-header-admin-view/publishing-header-admin-view.component';
import { SwitchAppsComponent } from '../components/switch-apps/switch-apps.component';
import { FetchPublishingDataRequest } from '../states/publishing.actions';
import { PublishingState } from '../states/publishing.state';

@Component({
  selector: 'rk-app-publishing-shell',
  templateUrl: './app-publishing-shell.component.html',
  styleUrls: ['./app-publishing-shell.component.scss'],
  imports: [
    UiModule,
    TranslatePipe,
    AndroidPublishingCardComponent,
    IosPublishingCardComponent,
    AsyncPipe,
    PaidPublishingBannerComponent,
    MatProgressSpinnerModule,
    SwitchAppsComponent,
    PublishingHeaderAdminViewComponent,
  ],
})
export class AppPublishingShellComponent implements OnInit, OnDestroy {
  private readonly subscription = new Subscription();

  currentAppId$: Observable<number> = inject(Store).select(AppState.currentAppId);
  appName$: Observable<string> = inject(Store).select(PublishingState.appName);
  appLogo$: Observable<string> = inject(Store).select(PublishingState.appLogo);
  isLoading$: Observable<boolean> = inject(Store).select(PublishingState.isLoading);
  publishingBannerCollapsed$: Observable<boolean> = inject(Store).select(
    PublishingState.publishingBannerCollapsed,
  );
  apps$: Observable<App[]> = inject(Store).select(AppState.apps);
  isAdmin$: Observable<boolean> = inject(Store).select(AuthState.isAdminOrSuperuser);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.route.params.subscribe(params => {
        const appId = params['appId'];
        if (appId) {
          this.setCurrentAppId(+appId);
        }
      }),
    );

    this.subscription.add(
      this.currentAppId$.subscribe(appId => {
        if (appId && !this.router.url.includes(`/publishing/${appId}`)) {
          this.router.navigateByUrl(`/publishing/${appId}`);
        }
      }),
    );

    this.subscription.add(
      this.currentAppId$
        .pipe(
          filter(appId => !!appId),
          distinctUntilChanged(),
        )
        .subscribe(appId => {
          this.fetchPublishingDataRequest(appId);
        }),
    );

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'PagePublishing',
        component: 'Page',
      }),
    );
    this.store.dispatch(new LogEvent('Page Viewed'));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  setCurrentAppId(id: number) {
    return this.store.dispatch(new SetCurrentApp(id));
  }

  fetchPublishingDataRequest(id: number) {
    return this.store.dispatch(new FetchPublishingDataRequest(id));
  }
}
